import React from 'react';
import { useSelector } from 'react-redux';
import { Ad, AppContent, ErrorBoundary } from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
  getAuthors,
  getBody,
  getBy,
  getCover,
  getCreatedAt,
  getCreatedAtDateFormatted,
  getCreatedAtTimeAndDateFormatted,
  getImageCover,
  getMainCategory,
  getMd5hashPostCanonicalUrl,
  getMetadataDescription,
  getNoIndex,
  getOgDescription,
  getOgImage,
  getOgTitle,
  getOpenWebId,
  getRevcontentWidgetID,
  getSeoDescription,
  getShareConfig,
  getShowRecommendationsAdvertisement,
  getSlideshowData,
  getSponsor,
  getTags,
  getTitle,
  getTwitterDescription,
  getTwitterImage,
  getTwitterTitle,
  getUpdatedAt,
  getUpdatedAtDateFormatted,
  getUpdatedAtTimeAndDateFormatted,
  getSeoTitle,
} from '../../store/template/postPage/postPageOdesa/postPageOdesa.selectors';
import { getAdSlotId, getCanonicalUrl, getSiteName, getTemplateName } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';

import { DefaultNavigationLayout } from '../../components/connectedComponents/DefaultNavigationLayout';
import { OdesaArticleComponent } from './OdesaArticleComponent';

interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const PostPageComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const title = useSelector(getTitle);
  const by = useSelector(getBy);
  const authors = useSelector(getAuthors);
  const updatedAtDateFormatted = useSelector(getUpdatedAtDateFormatted);
  const updatedAtTimeAndDateFormatted = useSelector(getUpdatedAtTimeAndDateFormatted);
  const createdAtDateFormatted = useSelector(getCreatedAtDateFormatted);
  const createdAtTimeAndDateFormatted = useSelector(getCreatedAtTimeAndDateFormatted);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showRecommendationsAdvertisement = useSelector(getShowRecommendationsAdvertisement);
  const revcontentWidgetID = useSelector(getRevcontentWidgetID);
  const slideshow = useSelector(getSlideshowData);
  const openWebId = useSelector(getOpenWebId);
  const tags = useSelector(getTags);
  const mainCategory = useSelector(getMainCategory);
  const pageDescription = useSelector(getMetadataDescription);
  const seoTitle = useSelector(getSeoTitle) || title;
  const pageImage = useSelector(getImageCover);
  const createdAt = useSelector(getCreatedAt);
  const updatedAt = useSelector(getUpdatedAt);
  const noIndex = useSelector(getNoIndex);
  const ogDescription = useSelector(getOgDescription);
  const ogTitle = useSelector(getOgTitle);
  const ogImage = useSelector(getOgImage);
  const twitterDescription = useSelector(getTwitterDescription);
  const twitterTitle = useSelector(getTwitterTitle);
  const twitterImage = useSelector(getTwitterImage);
  const seoDescription = useSelector(getSeoDescription);
  const articleSection = mainCategory ? mainCategory.displayName : '';
  const md5hashPostCanonicalUrl = useSelector(getMd5hashPostCanonicalUrl);

  return (
    <React.Fragment>
      <PostMetaTags
        {...{
          pageDescription,
          seoTitle,
          pageImage,
          createdAt,
          updatedAt,
          noIndex,
          ogDescription,
          ogTitle,
          ogImage,
          twitterDescription,
          twitterTitle,
          twitterImage,
          seoDescription,
          articleSection,
          authors,
        }}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <DefaultNavigationLayout mainWrapperConfigThemeKey="mainArticleContent">
            <Ad id={topAdId} />
            <OdesaArticleComponent {...{
              mainCategory,
              title,
              by,
              updatedAtDateFormatted,
              updatedAtTimeAndDateFormatted,
              authors,
              shareConfig,
              canonical,
              cover,
              siteName,
              templateName,
              sponsor,
              slideshow,
              body,
              sideBar1Id,
              sideBar2Id,
              showRecommendationsAdvertisement,
              revcontentWidgetID,
              createdAtDateFormatted,
              createdAtTimeAndDateFormatted,
              tags,
              md5hashPostCanonicalUrl,
              openWebId,
            }}
            />
            <Ad id={oneOnOneId} />
          </DefaultNavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
